import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import DocumentUpload from '../components/ui/DocumentUpload';
import FflDirectoryLookup, { directoryRecordToContact } from '../components/ui/FflDirectoryLookup';
import { FiSearch, FiPlus, FiEdit2, FiX, FiSave, FiUsers, FiShield } from 'react-icons/fi';
import { getFflStatus } from '../lib/fflStatus';
import { formatDate, parseDate } from '../lib/utils';

const FFL_CARD_CLASSES = {
  expired: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900',
  warning: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-900',
  ok: 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900',
};

const FFL_BANNER_CLASSES = {
  expired: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950/30 dark:border-yellow-900 dark:text-yellow-300',
  ok: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950/30 dark:border-green-900 dark:text-green-300',
};

export default function Contacts() {
  const { tenant } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newContact, setNewContact] = useState({
    type: 'DEALER', name: '', contact_name: '', ffl_number: '', ffl_expiration_date: '', phone: '', email: '', address: '', ffl_directory_number: null, ffl_last_verified_at: null
  });

  const fetchContacts = async () => {
    if (!tenant) return;
    setLoading(true);
    try {
      const data = await api.getContacts(tenant.id);
      setContacts(data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContacts(); }, [tenant]);

  const filtered = contacts.filter(c => {
    if (typeFilter === 'CUSTOMER' && c.type !== 'CUSTOMER') return false;
    if (typeFilter === 'DEALER' && c.type !== 'DEALER') return false;

    const term = searchTerm.toLowerCase();
    return (
      (c.name?.toLowerCase() || '').includes(term) ||
      (c.contact_name?.toLowerCase() || '').includes(term) ||
      (c.ffl_number?.toLowerCase() || '').includes(term) ||
      (c.email?.toLowerCase() || '').includes(term)
    );
  });

  const handleCreate = async () => {
    if (!newContact.name.trim()) return;
    setSaving(true);
    try {
      const contactData = {
        type: newContact.type,
        name: newContact.name.trim(),
        phone: newContact.phone.trim() || null,
        email: newContact.email.trim() || null,
        address: newContact.address.trim() || 'N/A'
      };
      if (newContact.type === 'DEALER') {
        contactData.contact_name = newContact.contact_name.trim() || null;
        contactData.ffl_number = newContact.ffl_number.trim() || null;
        contactData.ffl_expiration_date = newContact.ffl_expiration_date || null;
        contactData.ffl_directory_number = newContact.ffl_directory_number || null;
        contactData.ffl_last_verified_at = newContact.ffl_last_verified_at || null;
      } else {
        contactData.contact_name = null;
        contactData.ffl_number = null;
        contactData.ffl_expiration_date = null;
        contactData.ffl_directory_number = null;
        contactData.ffl_last_verified_at = null;
      }
      await api.createContact(tenant.id, contactData);
      setShowAdd(false);
      setNewContact({ type: 'DEALER', name: '', contact_name: '', ffl_number: '', ffl_expiration_date: '', phone: '', email: '', address: '', ffl_directory_number: null, ffl_last_verified_at: null });
      fetchContacts();
    } catch (err) {
      alert(`Error creating contact: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (contact) => {
    setEditingId(contact.id);
    setEditData({
      type: contact.type || 'DEALER',
      name: contact.name || '',
      contact_name: contact.contact_name || '',
      ffl_number: contact.ffl_number || '',
      ffl_expiration_date: contact.ffl_expiration_date || '',
      phone: contact.phone || '',
      email: contact.email || '',
      address: contact.address || '',
      ffl_directory_number: contact.ffl_directory_number || null,
      ffl_last_verified_at: contact.ffl_last_verified_at || null
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        type: editData.type,
        name: editData.name.trim(),
        phone: editData.phone.trim() || null,
        email: editData.email.trim() || null,
        address: editData.address.trim() || 'N/A'
      };
      if (editData.type === 'DEALER') {
        updateData.contact_name = editData.contact_name.trim() || null;
        updateData.ffl_number = editData.ffl_number.trim() || null;
        updateData.ffl_expiration_date = editData.ffl_expiration_date || null;
        updateData.ffl_directory_number = editData.ffl_directory_number || null;
        updateData.ffl_last_verified_at = editData.ffl_last_verified_at || null;
      } else {
        updateData.contact_name = null;
        updateData.ffl_number = null;
        updateData.ffl_expiration_date = null;
        updateData.ffl_directory_number = null;
        updateData.ffl_last_verified_at = null;
      }
      await api.updateContact(editingId, updateData);
      setEditingId(null);
      fetchContacts();
    } catch (err) {
      alert(`Error updating contact: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Contacts</h2>
          <p className="text-sm text-slate-500">Manage acquisition sources and disposition recipients.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
            <Input
              placeholder="Search contacts..."
              className="pl-9 w-[250px]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button onClick={() => setShowAdd(true)} icon={FiPlus}>Add Contact</Button>
        </div>
      </div>

      <div className="inline-flex rounded-md border border-slate-300 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800/50">
        {[
          { value: 'ALL', label: 'All' },
          { value: 'CUSTOMER', label: 'Customers' },
          { value: 'DEALER', label: 'Dealers' },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setTypeFilter(opt.value)}
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
              typeFilter === opt.value
                ? 'bg-white text-primary-700 shadow-sm dark:bg-slate-900 dark:text-primary-300'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {showAdd && (
        <Card className="border-primary-200 dark:border-primary-900 shadow-lg">
          <CardHeader>
            <CardTitle>New Contact</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewContact({ ...newContact, type: 'DEALER', ffl_number: '', contact_name: '' })}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                    newContact.type === 'DEALER'
                      ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300'
                      : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                  }`}
                >
                  Dealer / FFL
                </button>
                <button
                  type="button"
                  onClick={() => setNewContact({ ...newContact, type: 'CUSTOMER', ffl_number: '', contact_name: '' })}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                    newContact.type === 'CUSTOMER'
                      ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300'
                      : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                  }`}
                >
                  Customer
                </button>
              </div>
              {newContact.type === 'DEALER' && (
                <FflDirectoryLookup
                  onSelect={(rec) => setNewContact({ ...newContact, ...directoryRecordToContact(rec) })}
                />
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={newContact.type === 'DEALER' ? 'Business Name *' : 'Name *'}
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  placeholder={newContact.type === 'DEALER' ? 'e.g. Liberty Arms LLC' : 'e.g. John Smith'}
                  autoFocus
                />
                {newContact.type === 'DEALER' && (
                  <Input
                    label="FFL Number"
                    value={newContact.ffl_number}
                    onChange={(e) => setNewContact({ ...newContact, ffl_number: e.target.value })}
                    placeholder="e.g. 1-23-456-78-9A-01234"
                  />
                )}
              </div>
              {newContact.type === 'DEALER' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="FFL Expiration Date"
                    type="date"
                    value={newContact.ffl_expiration_date}
                    onChange={(e) => setNewContact({ ...newContact, ffl_expiration_date: e.target.value })}
                  />
                </div>
              )}
              <div className={`grid grid-cols-1 gap-4 ${newContact.type === 'DEALER' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                {newContact.type === 'DEALER' && (
                  <Input
                    label="Contact Name"
                    value={newContact.contact_name}
                    onChange={(e) => setNewContact({ ...newContact, contact_name: e.target.value })}
                    placeholder="e.g. John Smith"
                  />
                )}
                <Input
                  label="Phone"
                  type="tel"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
                <Input
                  label="Email"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="contact@example.com"
                />
              </div>
              <Input
                label="Address"
                value={newContact.address}
                onChange={(e) => setNewContact({ ...newContact, address: e.target.value })}
                placeholder="123 Main St, City, State ZIP"
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button
                  type="button"
                  onClick={handleCreate}
                  isLoading={saving}
                  disabled={!newContact.name.trim()}
                  icon={FiPlus}
                >
                  Add Contact
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading contacts...</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-slate-900 rounded-lg border border-dashed border-slate-300 dark:border-slate-800">
          <SafeIcon icon={FiUsers} className="h-12 w-12 text-slate-300 dark:text-slate-700 mb-3" />
          <p className="text-slate-500">{searchTerm || typeFilter !== 'ALL' ? 'No contacts match the current filter.' : 'No contacts yet. Add your first contact above.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((contact) => {
            const fflStatus = getFflStatus(contact);
            const editStatus = editingId === contact.id
              ? getFflStatus({ type: editData.type, ffl_expiration_date: editData.ffl_expiration_date })
              : null;
            return (
            <Card key={contact.id} className={fflStatus ? FFL_CARD_CLASSES[fflStatus.level] : undefined}>
              <CardContent className="p-4">
                {editingId === contact.id ? (
                  <div className="space-y-4">
                    {editStatus && (
                      <div className={`rounded-md border px-4 py-3 text-sm font-medium ${FFL_BANNER_CLASSES[editStatus.level]}`}>
                        {editStatus.message}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEditData({ ...editData, type: 'DEALER' })}
                        className={`flex-1 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                          editData.type === 'DEALER'
                            ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300'
                            : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                        }`}
                      >
                        Dealer / FFL
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditData({ ...editData, type: 'CUSTOMER' })}
                        className={`flex-1 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                          editData.type === 'CUSTOMER'
                            ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300'
                            : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                        }`}
                      >
                        Customer
                      </button>
                    </div>
                    {editData.type === 'DEALER' && (
                      <FflDirectoryLookup
                        onSelect={(rec) => setEditData({ ...editData, ...directoryRecordToContact(rec) })}
                      />
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label={editData.type === 'DEALER' ? 'Business Name *' : 'Name *'}
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        autoFocus
                      />
                      {editData.type === 'DEALER' && (
                        <Input
                          label="FFL Number"
                          value={editData.ffl_number}
                          onChange={(e) => setEditData({ ...editData, ffl_number: e.target.value })}
                        />
                      )}
                    </div>
                    {editData.type === 'DEALER' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="FFL Expiration Date"
                          type="date"
                          value={editData.ffl_expiration_date}
                          onChange={(e) => setEditData({ ...editData, ffl_expiration_date: e.target.value })}
                        />
                      </div>
                    )}
                    <div className={`grid grid-cols-1 gap-4 ${editData.type === 'DEALER' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                      {editData.type === 'DEALER' && (
                        <Input
                          label="Contact Name"
                          value={editData.contact_name}
                          onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })}
                        />
                      )}
                      <Input
                        label="Phone"
                        value={editData.phone}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      />
                      <Input
                        label="Email"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      />
                    </div>
                    <Input
                      label="Address"
                      value={editData.address}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    />
                    <DocumentUpload
                      contactId={contact.id}
                      documentType={editData.type === 'DEALER' ? 'FFL_LICENSE' : 'TAX_STAMP'}
                      label={editData.type === 'DEALER' ? 'FFL License Documents' : 'Tax Stamp Documents'}
                    />
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="secondary" onClick={() => setEditingId(null)} icon={FiX}>Cancel</Button>
                      <Button type="button" onClick={handleSave} isLoading={saving} disabled={!editData.name.trim()} icon={FiSave}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white truncate">{contact.name}</h3>
                        <Badge variant={contact.type === 'CUSTOMER' ? 'default' : 'primary'}>
                          {contact.type === 'CUSTOMER' ? 'Customer' : 'Dealer'}
                        </Badge>
                        {contact.ffl_number && (
                          <Badge variant="primary">FFL: {contact.ffl_number}</Badge>
                        )}
                        {contact.ffl_expiration_date && (
                          <Badge variant={parseDate(contact.ffl_expiration_date) < new Date() ? 'error' : 'default'}>
                            Exp: {formatDate(contact.ffl_expiration_date)}
                          </Badge>
                        )}
                        {contact.ffl_directory_number && (
                          <Badge variant="default">
                            <span className="inline-flex items-center gap-1">
                              <SafeIcon icon={FiShield} className="h-3 w-3" />
                              ATF-linked{contact.ffl_last_verified_at ? ` · ${new Date(contact.ffl_last_verified_at).toLocaleDateString()}` : ''}
                            </span>
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                        {contact.contact_name && <span>Contact: {contact.contact_name}</span>}
                        {contact.phone && <span>Phone: {contact.phone}</span>}
                        {contact.email && <span>Email: {contact.email}</span>}
                        {contact.address && contact.address !== 'N/A' && <span>Address: {contact.address}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleEdit(contact)}
                      className="shrink-0 inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
                    >
                      <SafeIcon icon={FiEdit2} className="h-3.5 w-3.5" />
                      Edit
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      <div className="text-xs text-slate-400 text-right">
        {filtered.length} contact{filtered.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
